import { useRef, useMemo, useCallback, memo } from "react";
import type {
  AutocompleteOwnerState,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
} from "@mui/material/Autocomplete";
import { createFilterOptions } from "@mui/material/useAutocomplete";
import type { AutocompleteHighlightChangeReason, FilterOptionsState } from "@mui/material/useAutocomplete";
import { mergeSlotProps, useEventCallback } from "@mui/material/utils";
import VirtualListbox from "./components/VirtualListbox";
import { StyledAutocomplete } from "./VirtualAutocomplete.components";
import { DEFAULT_ESTIMATED_ROW_HEIGHT_PX, GROUP_MARKER } from "./constants";
import type { ListboxVirtualizer, OptionTuple, RenderOptionContent, VirtualListboxConfig } from "./types";
import AutocompleteNoOptionsText from "components/Inputs/Autocomplete/components/AutocompleteNoOptionsText";

export type VirtualAutocompleteProps<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = Omit<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, "renderOption" | "renderGroup"> & {
  /**
   * Renders the content of each option row.
   *
   * This replaces Autocomplete's `renderOption`, which this component uses
   * internally
   *
   * Rows are measured after mount, so they may vary in height; keep
   * `estimateItemHeight` close to the typical row. Defaults to
   * `getOptionLabel`.
   */
  renderOptionContent?: RenderOptionContent<Value>;
  /** Group header content when `groupBy` is set. Defaults to a ListSubheader. */
  renderGroupLabel?: (group: string) => React.ReactNode;
  /**
   * Initial row-height guess in px. Rows are measured after mount, so this only
   * has to be close; it drives the scroll height before anything is rendered.
   */
  estimateItemHeight?: number;
  /**
   * Exact row height in px, for lists whose rows are all the same height.
   *
   * Rows are then placed from this number and never measured, which skips a
   * ResizeObserver per mounted row and the re-render their measurements cause.
   * Takes precedence over `estimateItemHeight`.
   */
  rowHeight?: number;
};

function VirtualAutocomplete<
  Value,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
>(props: VirtualAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) {
  type OwnerState = AutocompleteOwnerState<Value, Multiple, DisableClearable, FreeSolo>;

  const {
    renderOptionContent,
    renderGroupLabel,
    estimateItemHeight = DEFAULT_ESTIMATED_ROW_HEIGHT_PX,
    rowHeight,
    filterOptions,
    onHighlightChange,
    onInputChange,
    noOptionsText,
    slotProps,
    ...rest
  } = props;

  const virtualizerRef = useRef<ListboxVirtualizer | null>(null);
  const visibleOptionsRef = useRef<Value[]>([]);
  const optionRowIndexRef = useRef<number[]>([]);
  const resetScrollKeyRef = useRef("");

  /**
   * Stores the function that returns the label for a given option. This is used
   * to ensure that the label used in the input matches the label used in the rendered options.
   */
  const optionLabelRef = useRef<OwnerState["getOptionLabel"] | null>(null);

  const renderContentRef = useRef<RenderOptionContent<Value>>(() => null);
  renderContentRef.current = renderOptionContent ?? ((option: Value) => optionLabelRef.current?.(option));

  const renderGroupLabelRef = useRef(renderGroupLabel);
  renderGroupLabelRef.current = renderGroupLabel;

  const configRef = useRef<VirtualListboxConfig<Value>>({
    estimateItemHeight,
    rowHeight,
    virtualizerRef,
    optionRowIndexRef,
    renderContentRef,
    renderGroupLabelRef,
    resetScrollKeyRef,
  });
  configRef.current.estimateItemHeight = estimateItemHeight;
  configRef.current.rowHeight = rowHeight;

  const defaultFilter = useMemo(() => createFilterOptions<Value>(), []);

  const handleFilterOptions = useEventCallback((options: Value[], state: FilterOptionsState<Value>) => {
    const filtered = (filterOptions ?? defaultFilter)(options, state);
    visibleOptionsRef.current = filtered;
    return filtered;
  });

  // Autocomplete can highlight a row that is not mounted, so its own
  // scrollIntoView would no-op. Drive the virtualizer instead.
  const handleHighlightChange = useEventCallback(
    (event: React.SyntheticEvent, option: Value | null, reason: AutocompleteHighlightChangeReason) => {
      // Only scroll the virtualizer if the highlight change was not caused by mouse or touch events.
      if (option && reason !== "mouse" && reason !== "touch") {
        const optionIndex = visibleOptionsRef.current.indexOf(option);
        if (optionIndex >= 0) {
          const rowIndex = optionRowIndexRef.current[optionIndex] ?? optionIndex;
          virtualizerRef.current?.scrollToIndex(rowIndex, { align: "auto" });
        }
      }
      onHighlightChange?.(event, option, reason);
    }
  );

  const handleInputChange = useEventCallback(
    (event: React.SyntheticEvent, newInputValue: string, reason: Parameters<NonNullable<typeof onInputChange>>[2]) => {
      resetScrollKeyRef.current = newInputValue;
      onInputChange?.(event, newInputValue, reason);
    }
  );

  const renderOption = useCallback(
    (
      optionProps: OptionTuple<Value>[0],
      option: Value,
      state: AutocompleteRenderOptionState,
      ownerState: OwnerState
    ) => {
      optionLabelRef.current = ownerState.getOptionLabel;
      return [optionProps, option, state] as unknown as React.ReactNode;
    },
    []
  );

  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) =>
      [GROUP_MARKER, params.key, params.group, params.children] as unknown as React.ReactNode,
    []
  );

  const mergedSlotProps = useMemo(() => {
    const listbox = { component: VirtualListbox, config: configRef.current as VirtualListboxConfig<unknown> };

    return {
      ...slotProps,
      popupIndicator: mergeSlotProps(slotProps?.popupIndicator, { size: "small" }),
      clearIndicator: mergeSlotProps(slotProps?.clearIndicator, { size: "small" }),
      // The virtual listbox must win over the caller's listbox props
      listbox: mergeSlotProps(listbox, slotProps?.listbox ?? {}),
    } as AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>["slotProps"];
  }, [slotProps]);

  return (
    <StyledAutocomplete
      disableListWrap
      size="small"
      {...rest}
      noOptionsText={noOptionsText ?? <AutocompleteNoOptionsText />}
      filterOptions={handleFilterOptions}
      onHighlightChange={handleHighlightChange}
      onInputChange={handleInputChange}
      renderOption={renderOption}
      renderGroup={renderGroup}
      slotProps={mergedSlotProps}
    />
  );
}

export default memo(VirtualAutocomplete) as typeof VirtualAutocomplete;
